import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewContainerRef
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';

import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { FlexModule } from '@angular/flex-layout';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerToggle
} from '@angular/material/datepicker';
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatError,
  MatFormField,
  MatLabel,
  MatSuffix
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { format, parse } from 'date-fns';
import moment from 'moment';
import {
  NgxMatTimepickerComponent,
  NgxMatTimepickerModule
} from 'ngx-mat-timepicker';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, merge, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  map,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';
import { HireItemService, ProductService } from '../..';
import { HireItem, Item, Product } from '../../../models';
import {
  PriceSuggestion,
  ProductHireEstimateRequest
} from '../../../models/product-hire-estimate';
import { OffHireItemComponent } from '../../off-hire-item/off-hire-item.component';
import { ConfigService } from '../../services/config.service';
import { HireItemPriceUpdateService } from '../hire-price-structure-dialog/hire-item-price-update.service';
import { HirePriceStructureDialogComponent } from '../hire-price-structure-dialog/hire-price-structure-dialog.component';
import { HIREABLE_PRICE_UPDATE_IJ } from '../hire-price-structure-dialog/hireable-price-update';

interface OffHireItemFormGroup {
  id: FormControl<number>;
  charge: FormControl<number | null>;
  free_accessory: FormControl<boolean | null>;
  pricing_type: FormControl<string>;
  zero_price_reason: FormControl<string | null>;
  quantity_returned: FormControl<number>;
  defer_pricing: FormControl<boolean>;
  price_suggestion: FormControl<PriceSuggestion | null>;
  virtual_product: FormControl<boolean>;
  hireItem: FormControl<HireItem>;
}

@Component({
  selector: 'app-off-hire-dialog',
  templateUrl: './off-hire-dialog.component.html',
  styleUrls: ['./off-hire-dialog.component.css'],
  providers: [
    HireItemService,
    ProductService,
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { subscriptSizing: 'dynamic' }
    },
    {
      provide: HIREABLE_PRICE_UPDATE_IJ,
      useClass: HireItemPriceUpdateService
    }
  ],
  standalone: true,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    NgFor,
    OffHireItemComponent,
    FlexModule,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatIcon,
    MatSelect,
    MatOption,
    NgIf,
    MatDialogActions,
    MatButton,
    MatError,
    NgxMatTimepickerModule,
    MatLabel,
    AsyncPipe
  ]
})
export class OffHireDialogComponent implements OnInit, OnDestroy {
  items: Array<HireItem>;

  offHireDetails = new UntypedFormGroup({
    off_hire_date: new UntypedFormControl(moment(), Validators.required),
    off_hire_time: new UntypedFormControl(
      format(new Date(), 'HH:mm'),
      Validators.required
    ),
    location_in: new UntypedFormControl(null, Validators.required),
    items: new FormArray<FormGroup<OffHireItemFormGroup>>([])
  });

  dateTimeFormat = ConfigService.dates.longDateTimeFormat;
  isClosingAction = false;
  private ngUnsubscribe = new Subject<void>();
  docketId: number;
  customerId: number;
  currentUserId = 0;
  currency = 'GBP';
  currencyRate = 1;
  virtualProducts = [];
  locations$ = this.configService.locations.pipe(
    map((locations) => {
      if (!locations) {
        return [];
      }
      return locations;
    })
  );

  constructor(
    public dialogRef: MatDialogRef<OffHireDialogComponent>,
    private configService: ConfigService,
    private hireItemService: HireItemService,
    public dialog: MatDialog,
    private productService: ProductService,
    private vcr: ViewContainerRef,
    private toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data
  ) {
    this.configService.currentUser
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((user) => {
        this.currentUserId = user.id;
      });
    this.currencyRate = data.currencyRate;
    this.currency = data.currency;
    this.items = data.items;
    this.virtualProducts = data.items
      .filter((item) => item.hireable_type === 'product')
      .map((item) => item.id);
    this.docketId = data.docketId;
    this.customerId = data.customerId;
    this.isClosingAction = data.closingAction;
    this.configService.currentLocation
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((currentLocation) => {
        if (!currentLocation) {
          return;
        }
        this.offHireDetails.patchValue({ location_in: currentLocation.id });
      });
  }

  get itemsFormArray(): FormArray<FormGroup<OffHireItemFormGroup>> {
    return this.offHireDetails.get('items') as FormArray<
      FormGroup<OffHireItemFormGroup>
    >;
  }

  ngOnInit() {
    const offHireItems = this.items.map((item) => {
      return {
        id: item.id,
        charge: this.getItemDefaultChargeValue(item),
        free_accessory: item.free_accessory,
        pricing_type: item.pricing_type,
        zero_price_reason: item.zero_price_reason,
        quantity_returned: item.quantity,
        price_later: false,
        hireItem: item
      };
    });
    this.offHireDetails.setControl(
      'items',
      new FormArray<FormGroup<OffHireItemFormGroup>>(
        offHireItems.map((item) => {
          return new FormGroup({
            id: new FormControl<number>(item.id),
            free_accessory: new FormControl<boolean>(item.free_accessory === 1),
            defer_pricing: new FormControl<boolean>(false),
            charge: new FormControl<number | null>(item.charge),
            pricing_type: new FormControl<string>(item.pricing_type),
            quantity_returned: new FormControl<number>({
              value: item.quantity_returned,
              disabled: item.quantity_returned === 1
            }),
            zero_price_reason: new FormControl<string | null>(
              item.zero_price_reason
            ),
            price_suggestion: new FormControl<PriceSuggestion | null>(null),
            virtual_product: new FormControl<boolean>(
              this.virtualProducts.includes(item.id)
            ),
            hireItem: new FormControl<HireItem>(item.hireItem)
          });
        })
      )
    );
    this.getSuggestedPrices();
  }

  togglePriceLater(priceLater: boolean, itemId: number) {
    const itemFormGroup = this.offHireDetails
      .get('items')
      .get(itemId.toString()) as FormGroup;
    if (!itemFormGroup) {
      return;
    }
    const originalItem = this.items.find((item) => item.id === itemId);
    if (!originalItem) {
      return;
    }
    itemFormGroup.patchValue({
      defer_pricing: priceLater,
      charge: priceLater ? null : originalItem.charge,
      zero_price_reason: priceLater ? null : originalItem.zero_price_reason,
      free_accessory: priceLater ? null : originalItem.free_accessory
    });
    itemFormGroup.updateValueAndValidity();
  }

  saveOffHire() {
    const offHire = this.getOffHireDateTime();
    if (!offHire) {
      return;
    }
    const locationIn = this.offHireDetails.get('location_in').value;
    const itemsKeyedById = this.offHireDetails
      .get('items')
      .getRawValue()
      .map(({ hireItem, ...item }) => {
        return {
          ...item,
          off_hire: offHire.toISOString(),
          location_in: locationIn,
          off_hired_by: this.currentUserId
        };
      })
      .reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {});

    this.hireItemService
      .offHireItems(this.docketId, itemsKeyedById)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((response) => {
        this.items.forEach((item) => {
          Object.assign(item, itemsKeyedById[item.id]);
        });
        this.dialogRef.close(response);
      });
  }

  private getOffHireDateTime() {
    const offHireDate = this.offHireDetails.get('off_hire_date').value;
    const offHireTime = this.offHireDetails.get('off_hire_time').value;
    const offHire = parse(offHireTime, 'HH:mm', offHireDate?.toDate() ?? null);
    if (offHire.toString() === 'Invalid Date') {
      this.toastr.error('Invalid off hire date and time');
      return null;
    }
    return offHire;
  }

  private getSuggestedPrices() {
    const offHireDate = this.offHireDetails.get('off_hire_date');
    const offHireTime = this.offHireDetails.get('off_hire_time');
    return merge(offHireDate.valueChanges, offHireTime.valueChanges)
      .pipe(
        startWith(null),
        switchMap(() => {
          const offHire = this.getOffHireDateTime();
          if (!offHire) {
            return of(null);
          }
          const hireEstimates: ProductHireEstimateRequest[] = this.items.map(
            (item) => {
              return this.hireItemToPriceEstimatePayloadItem(item, offHire);
            }
          );
          return this.productService.getPrice(hireEstimates).pipe(
            catchError((err, obs) => {
              return of(null);
            })
          );
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((priceSuggestions) => {
        if (!priceSuggestions || priceSuggestions.length === 0) {
          return;
        }
        priceSuggestions.forEach((priceSuggestion) => {
          const itemFormGroup = (
            this.offHireDetails.get('items') as FormArray<
              FormGroup<OffHireItemFormGroup>
            >
          ).controls.find(
            (item) => item.value.id === priceSuggestion.hireable_id
          );
          if (!itemFormGroup) {
            console.warn(
              'No item form group found for price suggestion',
              priceSuggestion
            );
            return;
          }
          const charge = this.getItemChargeValue(
            priceSuggestion,
            itemFormGroup.value.pricing_type,
            itemFormGroup.value.charge
          );
          itemFormGroup.patchValue({
            price_suggestion: priceSuggestion,
            charge: charge
          });
        });
      });
  }

  private getItemChargeValue(
    priceSuggestion: PriceSuggestion,
    pricingType: string,
    currentCharge: number | null
  ): number | null {
    if (pricingType === 'fixed') {
      return currentCharge;
    }
    if (pricingType === 'no_charge') {
      return 0;
    }
    return priceSuggestion.estimate?.price;
  }

  private hireItemToPriceEstimatePayloadItem(
    item: HireItem,
    offHire: Date
  ): {
    hire_item_id: number;
    currency_code: string;
    // always use quantity of 1, figure out price for actual returned quantity later
    quantity: number;
    hireable_type: string;
    hireable_id: number;
    customer_id: number;
    product_id: number;
    on_hire: any;
    off_hire: string;
  } {
    return {
      hire_item_id: item.id,
      currency_code: this.currency,
      // always use quantity of 1, figure out price for actual returned quantity later
      quantity: 1,
      hireable_type: 'hireItem',
      hireable_id: item.id,
      customer_id: this.customerId,
      product_id:
        item.hireable_type === 'item'
          ? (item.hireable as Item).product_id
          : (item.hireable as Product).id,
      on_hire: item.on_hire,
      off_hire: offHire.toISOString()
    };
  }

  updateTime(event: any, field: string) {
    this.offHireDetails.get(field).patchValue(event);
  }

  updateTimeEnteredManually(field: string, picker: NgxMatTimepickerComponent) {
    const control = this.offHireDetails.get(field);
    if (!control.value) {
      return;
    }
    // 24 hr patterns to try and parse
    const parsePatterns = ['HH:mm', 'HH.mm', 'H:mm', 'HHmm', 'Hmm', 'HH', 'H'];
    const invalidValue = control.value;
    for (const pattern of parsePatterns) {
      const parsed = parse(invalidValue, pattern, new Date());
      if (parsed.toString() !== 'Invalid Date') {
        control.patchValue(format(parsed, 'HH:mm'));
        return picker.updateTime(control.value);
      }
    }
    control.setErrors({ invalidTime: true });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private getItemDefaultChargeValue(item: HireItem): number | null {
    if (item.pricing_type === 'fixed') {
      if (item.charge && this.currencyRate) {
        return item.charge * this.currencyRate;
      }
      return item.charge;
    }
    if (item.pricing_type === 'no_charge') {
      return 0;
    }
    return null;
  }

  getPricingType(item: HireItem): string {
    if (item.hireable && (item.hireable as Product).pricing_type) {
      return (item.hireable as Product).pricing_type;
    }
    return 'default';
  }

  openPriceStructureDialog(itemFormGroup: FormGroup<OffHireItemFormGroup>) {
    this.dialog
      .open(HirePriceStructureDialogComponent, {
        data: {
          hireItem: itemFormGroup.value.hireItem,
          currencyRate: this.currencyRate,
          currency: this.currency,
          docketId: this.docketId,
          customerId: this.customerId,
          pricingType: itemFormGroup.value.pricing_type,
          fixedPrice: itemFormGroup.value.charge,
          itemIsOffHired: false,
          hireableId: itemFormGroup.value.id,
          hireableType: 'hireItem',
          productId:
            itemFormGroup.value.hireItem.hireable_type === 'item'
              ? (itemFormGroup.value.hireItem.hireable as Item).product_id
              : (itemFormGroup.value.hireItem.hireable as Product).id
        },
        viewContainerRef: this.vcr
      })
      .afterClosed()
      .pipe(
        take(1),
        map((result) => {
          return { ...itemFormGroup.value, ...result };
        }),
        switchMap((result) => {
          // get suggested price for item and map it to the form group update
          return this.getSuggestedPriceForItem(result.hireItem).pipe(
            map(
              (
                suggestedPrice
              ): ReturnType<FormGroup<OffHireItemFormGroup>['getRawValue']> => {
                return {
                  ...result,
                  charge: this.getItemChargeValue(
                    suggestedPrice,
                    result.pricing_type,
                    result.charge
                  ),
                  price_suggestion: suggestedPrice
                };
              }
            )
          );
        })
      )
      .subscribe((result) => {
        // update item in form group
        itemFormGroup.patchValue({
          charge: result.charge,
          free_accessory: result.free_accessory,
          zero_price_reason: result.zero_price_reason,
          pricing_type: result.pricing_type,
          price_suggestion: result.price_suggestion
        });
        itemFormGroup.updateValueAndValidity();
      });
  }

  private getSuggestedPriceForItem(
    item: HireItem
  ): Observable<PriceSuggestion> {
    const offHire = this.getOffHireDateTime();
    if (!offHire) {
      return of(null);
    }
    const hireEstimates: ProductHireEstimateRequest[] = [
      this.hireItemToPriceEstimatePayloadItem(item, offHire)
    ];
    return this.productService.getPrice(hireEstimates).pipe(
      takeUntil(this.ngUnsubscribe),
      map((response) => {
        return response[0];
      })
    );
  }
}
